/**
 * The one copy of the terms.
 *
 * Shown when an account is created and again in Settings, from here, so that what somebody
 * agreed to and what they can go back and read are the same words. The version travels with
 * the acceptance: recording that a person agreed, without recording what they agreed to, stops
 * meaning anything the first time the wording changes.
 */
import { useLanguage, useT } from '../i18n/i18n';

export const TERMS_VERSION = '2026-10-19';

export const TERMS_SUMMARY =
  'A tool for running and recording teaching. It does not diagnose, treat, or replace the '
  + 'judgement of the person responsible for the learner.';

export default function Terms() {
  const t = useT();
  const { lang } = useLanguage();
  return (
    <div className="prose-terms">
      {lang !== 'en' && (
        <p className="field-hint">{t('This is a translation for your convenience. The English version is the one that applies.')}</p>
      )}
      <p className="field-hint">{t('Version {version}', { version: TERMS_VERSION })}</p>

      <h3>{t('What this is')}</h3>
      <p>
        {t('EasyLearning is a tool for running teaching activities with a learner and keeping a record of what happened. It presents what you have set up, records what the learner answered, and shows you the result.')}
      </p>

      <h3>{t('What this is not')}</h3>
      <p>
        {t("It is not a medical device, and it does not diagnose anything or provide treatment. Nothing it shows you is advice about a person's health, education or care. It does not decide what a learner should be taught, or when something has been learned well enough — you set those rules, and you can change them at any time.")}
      </p>
      <p>
        {t("It does not replace the judgement of whoever is responsible for the learner. Where a learner's programme is overseen by a qualified professional, this is somewhere to run and record that programme, not a substitute for the person overseeing it.")}
      </p>

      <h3>{t('Who may use it')}</h3>
      <p>
        {t('An account belongs to an adult. You may only add a learner and record their practice if you are responsible for them or have permission from whoever is. You are responsible for having that permission before you start.')}
      </p>

      <h3>{t('What is kept')}</h3>
      <p>
        {t("The names you choose for your learners, the programs you build, and a record of each try: what was shown, what was answered, whether it was right the first time, how long it took, and any rewards given. If you would rather not store a child's full name, use initials or a first name only — nothing here needs more than that.")}
      </p>
      <p>
        {t('You can download everything as a file at any time from the Report page, and deleting your account removes your sign-in, your learners, their programs and everything recorded about their practice. That cannot be undone.')}
      </p>

      <h3>{t('Your own pictures and voice')}</h3>
      <p>
        {t("The voice recordings and photos you choose to add are stored with the rest of your account's data. They are used only to show and play them back in your games and on the Talk Board, to the people using your account. They are not shared with anyone else, not sold, and not used to train any computer model.")}
      </p>
      <p>
        {t("You decide what to record and photograph. Please avoid photographing a learner's face, or anything that identifies where they live, unless it is needed for what you are teaching. Recordings of your own voice, or of someone who has agreed, are what this is for.")}
      </p>
      <p>
        {t('You can remove any recording or photo at any time, and it is deleted straight away. If your plan ends, they stop being used, and 30 days later they are deleted unless you have subscribed again. Deleting your account deletes them at once.')}
      </p>
      <p>
        {t('Every plan, and Talk Board on its own, includes 100 photos and 100 recordings for the account. With the Custom pictures and voice add-on, fair use is 100 MB for each place on your plan, or each learner if you have Talk Board without a plan — around 500 photos with a recording each. Each photo or recording can be up to 1.5 MB, and recordings up to 10 seconds. When an allowance is used up, nothing is deleted: new ones wait until you remove some you no longer need, or add the add-on or more places. If the add-on ends, nothing is deleted either.')}
      </p>

      <h3>{t('Paying for it')}</h3>
      <p>
        {t('New accounts start with a free trial. After that a subscription is needed to use programs. You can pay monthly, every six months or yearly; paying ahead costs less. A subscription renews automatically at the end of each period until you cancel it.')}
      </p>
      <p>
        {t('Add-ons — Custom pictures and voice, and Talk Board — are charged on the same schedule as your plan, for every place on the plan. Talk Board can also be bought on its own, without a plan, and is then charged for each learner on the account.')}
      </p>
      <p>
        {t('You can pay by card or by Direct Debit from a UK bank account. Payment and cancelling are handled by Stripe, and your card or bank details are never held here. If you choose Direct Debit, Stripe emails you to confirm the Direct Debit and before each payment is taken, and your payments are protected by the Direct Debit Guarantee. A Direct Debit payment takes a few working days to clear; you can use your subscription while it does, but if the payment fails, access stops until it is paid. You can cancel at any time, and you keep access until the end of the period you have paid for.')}
      </p>

      <h3>{t('Keeping it available')}</h3>
      <p>
        {t('We work to keep this running, but we cannot promise it will always be available, and we do not guarantee it will be free of faults. Anything you would be sorry to lose should be downloaded and kept somewhere of your own.')}
      </p>

      <h3>{t('When this changes')}</h3>
      <p>
        {t('If these terms change in a way that matters, you will be asked to read them again before carrying on. The version above tells you which wording you are looking at.')}
      </p>

      <h3>{t('Getting in touch')}</h3>
      <p>
        {t('Questions, or anything that looks wrong:')}{' '}
        <a href="mailto:akbar@fractalon.net">akbar@fractalon.net</a>.
      </p>
    </div>
  );
}
